import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe, Loader2 } from "lucide-react";

const DEMO = [
  ["employee@essar.com", "Employee (Zurich→Houston)"],
  ["comp@essar.com", "Compensation Admin"],
  ["mobility@essar.com", "Mobility Specialist"],
  ["tax@essar.com", "Tax Reviewer"],
  ["auditor@essar.com", "Auditor"],
  ["admin@essar.com", "System Administrator"],
];

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@mesabiglobalmobility.com");
  const [password, setPassword] = useState("Mesabi@123");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [notice, setNotice] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setLoading(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email });
        setNotice(data.message || "If an account exists for that email, a reset link has been sent.");
      } else if (mode === "register") {
        await register(name, email, password);
        nav("/calculator");
      } else {
        await login(email, password);
        nav("/calculator");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between brand-gradient text-white p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-2 py-1.5 flex items-center"><img src="/mesabi-logo.png" alt="Mesabi Metallics" className="h-8 w-auto" /></div>
            <div>
              <div className="font-display font-extrabold text-xl tracking-tight leading-none">Mesabi Metallics</div>
              <div className="text-xs uppercase tracking-[0.2em] text-gold">Global Mobility</div>
            </div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">
            Global Mobility<br /><span className="text-gold">Intelligence</span> Model
          </h1>
          <p className="mt-3 text-gold font-semibold tracking-wide">The Essar Metals &amp; Mining Mobility Calculator</p>
          <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
            A rigorous home-based balance-sheet methodology that preserves employee purchasing
            power — with full traceability, source governance and executive reporting.
          </p>
          <div className="mt-8 flex gap-6 text-sm text-slate-300">
            <div><div className="font-mono text-2xl font-bold text-white">3</div>employee inputs</div>
            <div><div className="font-mono text-2xl font-bold text-white">±COLA</div>balance sheet</div>
            <div><div className="font-mono text-2xl font-bold text-white">100%</div>traceable</div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-400">Provisional brand tokens — pending official ESSAR brand kit.</div>
        <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-essred/20 blur-3xl" />
        <div className="absolute right-10 bottom-10 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="bg-white rounded-md px-1.5 py-1 flex items-center border"><img src="/mesabi-logo.png" alt="Mesabi Metallics" className="h-7 w-auto" /></div>
            <span className="font-display font-bold text-lg">Mesabi Metallics</span>
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">{mode === "register" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Sign in"}</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">{mode === "register" ? "Register with your work email to get started." : mode === "forgot" ? "Enter your work email and we'll send you a reset link." : "Access your assignment intelligence workspace."}</p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} className="mt-1.5" required />
            </div>
            {mode !== "forgot" && (
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" data-testid="login-password-input" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className="mt-1.5" required />
              </div>
            )}
            {error && <div data-testid="login-error" className="text-sm text-essred bg-essred/5 border border-essred/20 rounded-lg px-3 py-2">{error}</div>}
            {notice && <div data-testid="login-notice" className="text-sm text-emerald-600 bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3 py-2">{notice}</div>}
            <Button data-testid="login-submit-btn" type="submit" disabled={loading}
              className="w-full bg-[#F5A623] hover:bg-[#e0961a] text-[#0A0E14] h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "register" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in")}
            </Button>
          </form>
          <div className="mt-4 flex items-center justify-between">
            <button type="button" data-testid="auth-mode-toggle" onClick={() => { setError(""); setNotice(""); setMode(mode === "login" ? "register" : "login"); }} className="text-sm text-[#B45309] hover:underline">
              {mode === "register" ? "Already registered? Sign in" : mode === "forgot" ? "Back to sign in" : "New here? Create an account"}
            </button>
            {mode === "login" && (
              <button type="button" data-testid="forgot-password-link" onClick={() => { setError(""); setNotice(""); setMode("forgot"); }} className="text-sm text-slate-500 hover:text-[#B45309] hover:underline">
                Forgot password?
              </button>
            )}
          </div>
          <div className="mt-6 pt-5 border-t">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Admin: admin@mesabiglobalmobility.com / Mesabi@123 · demo staff pwd Essar@2026</div>
            <div className="grid grid-cols-1 gap-1.5">
              {DEMO.map(([em, lbl]) => (
                <button key={em} data-testid={`demo-login-${em.split("@")[0]}`}
                  onClick={() => { setEmail(em); setPassword("Essar@2026"); }}
                  className="text-left text-xs px-3 py-2 rounded-lg border hover:border-[#D97706] hover:bg-slate-100 transition-colors flex justify-between">
                  <span className="font-medium text-slate-700">{lbl}</span>
                  <span className="text-slate-500 font-mono">{em}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
